import {
  Badge,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminActionModal } from '../../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../../components/admin-shell/AdminShell';
import { AdminTableSection } from '../../../components/admin-shell/AdminTableSection';
import { showToast } from '../../../lib/toast';
import { adminPost } from '../_api';
import { usePaginatedQuery } from '../_hooks';

interface UserRow {
  id: string;
  email: string;
  display_name: string;
  role: 'artist' | 'host' | 'admin';
  status: 'pending' | 'active' | 'suspended' | 'deleted';
  created_at: string;
  last_login_at: string | null;
}

export const Route = createFileRoute('/admin/users/')({
  component: AdminUsersList,
});

type CreateRole = 'artist' | 'host' | 'admin';

const EMPTY_FORM = {
  email: '',
  display_name: '',
  password: '',
  role: 'admin' as CreateRole,
  grant_admin: false,
};

function AdminUsersList() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error, page, setPage, totalPages, bindFilter } = usePaginatedQuery<UserRow>({
    queryKey: ['admin', 'users'],
    endpoint: '/api/admin/users',
    filters: { q, role, status },
  });

  // ---- Create user ---------------------------------------------------------
  const [createOpen, createCtl] = useDisclosure(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [createError, setCreateError] = useState<string | null>(null);

  function patchForm(patch: Partial<typeof EMPTY_FORM>) {
    setForm((f) => ({ ...f, ...patch }));
  }
  function resetCreate() {
    setForm(EMPTY_FORM);
    setCreateError(null);
  }

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const nameOk = form.display_name.trim().length >= 2;
  const passwordOk = form.password.length >= 10;
  const canCreate = emailOk && nameOk && passwordOk;
  const willBeAdmin = form.role === 'admin' || form.grant_admin;

  const createUser = useMutation({
    mutationFn: () =>
      adminPost<{ user: { id: string; email: string } }>('/api/admin/users', {
        email: form.email.trim(),
        display_name: form.display_name.trim(),
        password: form.password,
        role: form.role,
        grant_admin: form.role === 'admin' ? true : form.grant_admin,
      }),
    onSuccess: (res) => {
      createCtl.close();
      resetCreate();
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      showToast({
        kind: 'success',
        body: `Created ${res.user.email}. Share the password with them so they can sign in.`,
      });
    },
    onError: (e) => setCreateError(e instanceof Error ? e.message : 'Could not create the user.'),
  });

  return (
    <AdminShell>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={2}>Users</Title>
          <Button
            onClick={() => {
              resetCreate();
              createCtl.open();
            }}
          >
            Create user
          </Button>
        </Group>

        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search email or name"
            value={q}
            onChange={(e) => bindFilter(setQ)(e.currentTarget.value)}
            style={{ flexGrow: 1, minWidth: 240 }}
          />
          <Select
            placeholder="Role"
            clearable
            data={['artist', 'host', 'admin']}
            value={role}
            onChange={bindFilter(setRole)}
            style={{ minWidth: 140 }}
          />
          <Select
            placeholder="Status"
            clearable
            data={['pending', 'active', 'suspended', 'deleted']}
            value={status}
            onChange={bindFilter(setStatus)}
            style={{ minWidth: 160 }}
          />
        </Group>

        <AdminTableSection<UserRow>
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No users match those filters."
          header={
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Last login</Table.Th>
              <Table.Th>Joined</Table.Th>
            </Table.Tr>
          }
          renderRow={(u) => (
            <Table.Tr key={u.id}>
              <Table.Td>
                <Link to="/admin/users/$id" params={{ id: u.id }}>
                  {u.email}
                </Link>
              </Table.Td>
              <Table.Td>{u.display_name}</Table.Td>
              <Table.Td>
                <Badge variant="light">{u.role}</Badge>
              </Table.Td>
              <Table.Td>
                <Badge color={statusColor(u.status)} variant="light">
                  {u.status}
                </Badge>
              </Table.Td>
              <Table.Td>{u.last_login_at ? formatDate(u.last_login_at) : '–'}</Table.Td>
              <Table.Td>{formatDate(u.created_at)}</Table.Td>
            </Table.Tr>
          )}
        />
      </Stack>

      <AdminActionModal
        opened={createOpen}
        onClose={() => {
          createCtl.close();
          resetCreate();
        }}
        title="Create user"
        onConfirm={() => {
          setCreateError(null);
          createUser.mutate();
        }}
        confirmLabel={willBeAdmin ? 'Create admin' : 'Create user'}
        loading={createUser.isPending}
        confirmDisabled={!canCreate}
        error={createError}
      >
        <TextInput
          label="Email"
          placeholder="name@example.com"
          value={form.email}
          onChange={(e) => patchForm({ email: e.currentTarget.value })}
          error={form.email.length > 0 && !emailOk ? 'Enter a valid email.' : null}
          required
        />
        <TextInput
          label="Display name"
          placeholder="Richie Fambro"
          value={form.display_name}
          onChange={(e) => patchForm({ display_name: e.currentTarget.value })}
          error={form.display_name.length > 0 && !nameOk ? 'At least 2 characters.' : null}
          required
        />
        <PasswordInput
          label="Temporary password"
          description="At least 10 characters. Share it with them directly — they can change it in Settings."
          value={form.password}
          onChange={(e) => patchForm({ password: e.currentTarget.value })}
          error={form.password.length > 0 && !passwordOk ? 'At least 10 characters.' : null}
          required
        />
        <Select
          label="Role"
          data={[
            { value: 'admin', label: 'Admin (staff / ops — no artist or host profile)' },
            { value: 'host', label: 'Host (owns a venue account)' },
            { value: 'artist', label: 'Artist (has an artist profile)' },
          ]}
          value={form.role}
          onChange={(v) =>
            patchForm({ role: (v as CreateRole | null) ?? 'admin', grant_admin: false })
          }
          allowDeselect={false}
        />
        {form.role === 'admin' ? (
          <Text size="sm" c="dimmed">
            Admin accounts always have full admin access.
          </Text>
        ) : (
          <Checkbox
            label="Also grant admin access"
            checked={form.grant_admin}
            onChange={(e) => patchForm({ grant_admin: e.currentTarget.checked })}
          />
        )}
      </AdminActionModal>
    </AdminShell>
  );
}

function statusColor(status: string): string {
  if (status === 'active') return 'tide';
  if (status === 'suspended') return 'yellow';
  if (status === 'deleted') return 'gray';
  return 'blue';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
